/**
 * Geofence Alerts Service (Dashboard side)
 * Listens for geofence events from the server-side trigger
 * and provides real-time alerts to the dispatcher/owner
 */

import { supabase } from '../supabaseClient';

/**
 * Fetch unprocessed geofence alerts
 */
export const getGeofenceAlerts = async (limit = 50) => {
  const { data, error } = await supabase
    .from('geofence_events')
    .select(`
      id,
      event_type,
      stop_type,
      address,
      distance_meters,
      dwell_seconds,
      created_at,
      load_id,
      load_stop_id,
      driver:drivers(name, phone),
      load:loads(load_number, status)
    `)
    .eq('processed', false)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching geofence alerts:', error);
    return [];
  }
  return data || [];
};

/**
 * Mark a geofence event as processed (dispatcher acknowledged)
 */
export const dismissGeofenceAlert = async (eventId) => {
  const { error } = await supabase
    .from('geofence_events')
    .update({ processed: true })
    .eq('id', eventId);

  return !error;
};

/**
 * Mark all alerts for a specific load as processed
 */
export const dismissAlertsForLoad = async (loadId) => {
  const { error } = await supabase
    .from('geofence_events')
    .update({ processed: true })
    .eq('load_id', loadId)
    .eq('processed', false);

  return !error;
};

/**
 * Subscribe to real-time geofence events
 * Returns unsubscribe function
 */
export const subscribeToGeofenceEvents = (onEvent) => {
  const channel = supabase
    .channel('geofence_realtime')
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'geofence_events',
    }, (payload) => {
      onEvent(payload.new);
    })
    .subscribe();

  console.log('Subscribed to geofence events');
  return () => supabase.removeChannel(channel);
};

/**
 * Get suggested status based on geofence event
 */
export const getSuggestedStatus = (event, currentLoadStatus) => {
  if (event.event_type !== 'dwell') return null;

  if (event.stop_type === 'pickup') {
    switch (currentLoadStatus) {
      case 'assigned':
      case 'en_route':
        return 'at_pickup';
      case 'at_pickup':
        return 'loading';
      default:
        return null;
    }
  }

  if (event.stop_type === 'delivery') {
    switch (currentLoadStatus) {
      case 'in_transit':
        return 'at_delivery';
      case 'at_delivery':
        return 'unloading';
      default:
        return null;
    }
  }

  return null;
};

/**
 * Format a geofence event for display
 */
export const formatGeofenceAlert = (event) => {
  const driverName = event.driver?.name || 'Unknown Driver';
  const loadNum = event.load?.load_number || '?';
  const address = event.address || 'Unknown location';
  const distance = event.distance_meters ? `${Math.round(event.distance_meters)}m` : '';
  const dwellMins = event.dwell_seconds ? Math.round(event.dwell_seconds / 60) : 0;

  switch (event.event_type) {
    case 'enter':
      return {
        icon: '📍',
        title: `${driverName} arrived`,
        detail: `Near ${event.stop_type} for Load #${loadNum}`,
        sub: `${address} (${distance} away)`,
        priority: 'info',
      };
    case 'dwell':
      return {
        icon: '⏱️',
        title: `${driverName} — ${dwellMins} min at ${event.stop_type}`,
        detail: `Load #${loadNum} — suggest status update`,
        sub: address,
        priority: 'action',
      };
    case 'exit':
      return {
        icon: '🚚',
        title: `${driverName} departed`,
        detail: `Left ${event.stop_type} for Load #${loadNum} (was there ${dwellMins} min)`,
        sub: address,
        priority: 'info',
      };
    default:
      return {
        icon: '📌',
        title: `Geofence: ${event.event_type}`,
        detail: `${driverName} — Load #${loadNum}`,
        sub: address,
        priority: 'info',
      };
  }
};

export default {
  getGeofenceAlerts,
  dismissGeofenceAlert,
  dismissAlertsForLoad,
  subscribeToGeofenceEvents,
  getSuggestedStatus,
  formatGeofenceAlert,
};
